import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// Merchandiser Document & Approval Tracker — rendered as the
// "Approvals & Documents" tab inside Order Execution. Per selected GPO:
// A) Sample approvals  B) Test reports  C) Buyer submissions  D) Inspection reports.

const API_ORIGIN = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3000';
const fileHref = (u) => !u ? '#' : (/^https?:\/\//.test(u) ? u : (u.startsWith('/api/') ? API_ORIGIN + u : u));
const isImg = (u) => /\.(png|jpe?g|gif|webp)$/i.test(u || '');
const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const today = () => new Date().toISOString().slice(0, 10);

const SAMPLE_TYPES = [
  { key: 'proto', label: 'Proto' }, { key: 'fit', label: 'Fit' }, { key: 'size_set', label: 'Size Set' },
  { key: 'pp', label: 'PP' }, { key: 'salesman', label: 'Salesman' }, { key: 'shipment', label: 'Shipment' },
];
const SAMPLE_STATUS = {
  pending: { bg: '#f0f2f8', c: '#666', label: 'Pending' },
  sent: { bg: '#EEEDF8', c: '#3B3A8F', label: 'Sent' },
  approved: { bg: '#EAF7EE', c: '#166534', label: 'Approved' },
  rejected: { bg: '#FCEBEB', c: '#A32D2D', label: 'Rejected' },
  revision_requested: { bg: '#FFF8E6', c: '#9A6B00', label: 'Revision' },
};
// Strike-off (print/artwork) buyer response — its own domain (has approved_with_comments).
const SO_RESP = {
  pending: { bg: '#f0f2f8', c: '#666', label: 'Pending' },
  approved: { bg: '#EAF7EE', c: '#166534', label: 'Approved' },
  approved_with_comments: { bg: '#FFF8E6', c: '#9A6B00', label: 'Approved w/ comments' },
  rejected: { bg: '#FCEBEB', c: '#A32D2D', label: 'Rejected' },
};
const SO_METHODS = [['courier', 'Courier'], ['email', 'Email'], ['whatsapp', 'WhatsApp'], ['hand_delivery', 'Hand Delivery'], ['digital', 'Digital Proof']];
const soApproved = (r) => r === 'approved' || r === 'approved_with_comments';
const soTag = (r) => { const t = SO_RESP[r] || SO_RESP.pending; return { padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: t.bg, color: t.c }; };
const TEST_TYPES = [['fpt', 'FPT'], ['gpt', 'GPT'], ['wash', 'Wash Test'], ['lab', 'Lab Test'], ['other', 'Other']];
const INSPECTION_TYPES = [['inline', 'Inline Inspection'], ['final', 'Final Inspection'], ['aql', 'AQL Report'], ['buyer_inspection', 'Buyer Inspection']];
const RESULT_TAG = {
  pass: { bg: '#EAF7EE', c: '#166534' }, fail: { bg: '#FCEBEB', c: '#A32D2D' }, conditional: { bg: '#FFF8E6', c: '#9A6B00' },
  approved: { bg: '#EAF7EE', c: '#166534' }, rejected: { bg: '#FCEBEB', c: '#A32D2D' }, pending: { bg: '#f0f2f8', c: '#666' },
};

const inputStyle = { width: '100%', padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' };
const labelStyle = { fontSize: 10, color: '#888', fontWeight: 600, display: 'block', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.4 };
const secStyle = { fontSize: 12, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1, margin: '22px 0 10px', paddingBottom: 5, borderBottom: '2px solid #EEEDF8' };
const btnPrimary = { padding: '7px 16px', background: '#3B3A8F', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 };
const tag = (s) => { const t = RESULT_TAG[s] || RESULT_TAG.pending; return { padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: t.bg, color: t.c }; };

// Multi-file upload with thumbnails. value = array of /api/v1/files/... urls.
function PhotoUpload({ value = [], onChange, label = 'Upload photos' }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const files = [...e.target.files]; if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const r = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange([...(value || []), ...(r.data.files || []).map(f => f.url)]);
    } catch (err) { alert('Upload failed: ' + (err.response?.data?.error || err.message)); }
    finally { setBusy(false); e.target.value = ''; }
  };
  return (
    <div>
      <label style={{ ...btnPrimary, display: 'inline-block', background: '#EEEDF8', color: '#3B3A8F', border: '1px dashed #3B3A8F' }}>
        {busy ? 'Uploading…' : `📎 ${label}`}
        <input type="file" multiple accept="image/*,.pdf" style={{ display: 'none' }} onChange={upload} />
      </label>
      {(value || []).length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          {value.map((u, i) => (
            <div key={i} style={{ position: 'relative' }}>
              <a href={fileHref(u)} target="_blank" rel="noreferrer">
                {isImg(u)
                  ? <img src={fileHref(u)} alt="" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd' }} />
                  : <div style={{ width: 64, height: 64, borderRadius: 6, border: '1px solid #ddd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, background: '#F7F6F3' }}>📄</div>}
              </a>
              <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))}
                style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: 9, border: 'none', background: '#E22D2D', color: 'white', fontSize: 10, cursor: 'pointer', lineHeight: '18px', padding: 0 }}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Single-file upload returning one url (for test/inspection report files).
function FileUpload({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(r.data.url);
    } catch (err) { alert('Upload failed: ' + (err.response?.data?.error || err.message)); }
    finally { setBusy(false); e.target.value = ''; }
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <label style={{ ...btnPrimary, background: '#EEEDF8', color: '#3B3A8F', border: '1px dashed #3B3A8F' }}>
        {busy ? 'Uploading…' : value ? '📎 Replace file' : '📎 Upload report (PDF / image)'}
        <input type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={upload} />
      </label>
      {value && <a href={fileHref(value)} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#166534', fontWeight: 600 }}>✅ {String(value).split('/').pop()}</a>}
    </div>
  );
}

export default function ApprovalsDocs({ gpos, user }) {
  const [gpoNo, setGpoNo] = useState('');
  const [data, setData] = useState({ samples: [], tests: [], submissions: [], inspections: [], quality_final_inspections: [], strikeoffs: [], progress: null });
  const [sampleModal, setSampleModal] = useState(null);   // { type, record|null }
  const [strikeoffModal, setStrikeoffModal] = useState(false);
  const [testModal, setTestModal] = useState(null);       // { report_type } | true
  const [subModal, setSubModal] = useState(false);
  const [inspModal, setInspModal] = useState(null);
  const [saving, setSaving] = useState(false);

  const activeGpos = (gpos || []).filter(g => !g.is_superseded);

  const empty = { samples: [], tests: [], submissions: [], inspections: [], quality_final_inspections: [], strikeoffs: [], progress: null };
  const load = useCallback(async () => {
    if (!gpoNo) { setData(empty); return; }
    const r = await api.get('/merchdocs/all?gpo_no=' + encodeURIComponent(gpoNo)).catch(() => ({ data: { data: empty } }));
    setData(r.data.data || empty);
  }, [gpoNo]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const photos = (v) => { try { return typeof v === 'string' ? JSON.parse(v || '[]') : (v || []); } catch { return []; } };
  const latestSample = (type) => data.samples.filter(s => s.sample_type === type).sort((a, b) => b.revision_no - a.revision_no)[0] || null;
  const sampleHistory = (type) => data.samples.filter(s => s.sample_type === type).sort((a, b) => b.revision_no - a.revision_no).slice(1);

  if (!gpoNo) {
    return (
      <div>
        <div style={{ marginBottom: 14, padding: '10px 14px', background: '#EEEDF8', borderRadius: 6, fontSize: 13, color: '#3B3A8F' }}>
          📁 Merchandiser Document & Approval Tracker — sample approvals, test reports, buyer submissions and inspection reports per GPO.
          T&A activities <strong>cannot be marked done</strong> without the required documents here.
        </div>
        <div style={{ maxWidth: 360 }}>
          <label style={labelStyle}>Select GPO</label>
          <select style={inputStyle} value={gpoNo} onChange={e => setGpoNo(e.target.value)}>
            <option value="">— choose a GPO —</option>
            {activeGpos.map(g => <option key={g.id} value={g.gpo_no}>{g.gpo_no} · {g.style_no || ''} {g.brand ? '· ' + g.brand : ''}</option>)}
          </select>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 280 }}>
          <label style={labelStyle}>GPO</label>
          <select style={inputStyle} value={gpoNo} onChange={e => setGpoNo(e.target.value)}>
            {activeGpos.map(g => <option key={g.id} value={g.gpo_no}>{g.gpo_no} · {g.style_no || ''} {g.brand ? '· ' + g.brand : ''}</option>)}
          </select>
        </div>
        <div style={{ fontSize: 12, color: '#888', marginTop: 14 }}>
          {data.samples.length} sample record(s) · {data.tests.length} test report(s) · {data.submissions.length} submission(s) · {data.inspections.length} inspection report(s)
        </div>
        {data.progress && (
          <div style={{ marginLeft: 'auto', minWidth: 220, marginTop: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
              <span style={{ fontWeight: 700, color: '#3B3A8F' }}>APPROVALS & DOCUMENTS COMPLETION</span>
              <span style={{ fontWeight: 800, color: data.progress.pct >= 80 ? '#166534' : data.progress.pct >= 40 ? '#9A6B00' : '#A32D2D' }}>
                {data.progress.pct}% ({data.progress.done}/{data.progress.total})</span>
            </div>
            <div style={{ background: '#eee', borderRadius: 6, height: 10, overflow: 'hidden' }}>
              <div style={{ width: data.progress.pct + '%', height: '100%', background: data.progress.pct >= 80 ? '#166534' : data.progress.pct >= 40 ? '#f59e0b' : '#E22D2D', transition: 'width .3s' }} />
            </div>
          </div>
        )}
      </div>

      {/* FPT/GPT failure banner */}
      {data.tests.some(t => ['fpt', 'gpt'].includes(t.report_type) && t.result === 'fail') && (
        <div style={{ marginTop: 12, background: '#FCEBEB', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 16px', color: '#A32D2D', fontWeight: 700, fontSize: 13 }}>
          ⚠️ Test Failed — management notified.{' '}
          {data.tests.filter(t => ['fpt', 'gpt'].includes(t.report_type) && t.result === 'fail').map(t => t.report_type.toUpperCase() + (t.test_house ? ` (${t.test_house})` : '')).join(', ')}
        </div>
      )}

      {/* ── A) SAMPLE APPROVALS ── */}
      <div style={secStyle}>A · Sample Approvals</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
        {SAMPLE_TYPES.map(({ key, label }) => {
          const s = latestSample(key);
          const st = SAMPLE_STATUS[s?.status] || SAMPLE_STATUS.pending;
          const hist = sampleHistory(key);
          // Card colorization: approved green, rejected red, revision amber.
          const cardBg = s?.status === 'approved' ? '#EAF7EE' : s?.status === 'rejected' ? '#FCEBEB'
            : s?.status === 'revision_requested' ? '#FFF8E6' : 'white';
          const cardBorder = s?.status === 'approved' ? '#bbe5c5' : s?.status === 'rejected' ? '#fca5a5'
            : s?.status === 'revision_requested' ? '#f6d195' : '#eee';
          const card = (
            <div key={key} onClick={() => setSampleModal({ type: key, record: s })}
              style={{ border: `1px solid ${cardBorder}`, borderRadius: 10, padding: '12px 14px', cursor: 'pointer', background: cardBg, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 700, fontSize: 13 }}>{label} Sample</span>
                <span style={tag(s?.status === 'revision_requested' ? 'pending' : s?.status)}>{s ? st.label : 'Not started'}</span>
              </div>
              <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
                {s ? <>Rev {s.revision_no} · Made {fmtD(s.made_date)} · Dispatched {fmtD(s.dispatch_date)}{s.tracking_no ? ` · ${s.courier_name || ''} ${s.tracking_no}` : ''}</> : 'Click to log this sample'}
              </div>
              {hist.filter(h => h.status === 'rejected' || h.status === 'revision_requested').length > 0 && (
                <div style={{ fontSize: 11, color: '#B45309', marginTop: 4, fontWeight: 600 }}>⚠ {hist.filter(h => h.status === 'rejected' || h.status === 'revision_requested').length} prior rejection(s) — open to read reasons</div>
              )}
              {s && photos(s.submission_photos).length > 0 && (
                <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                  {photos(s.submission_photos).slice(0, 4).map((u, i) => isImg(u)
                    ? <img key={i} src={fileHref(u)} alt="" style={{ width: 34, height: 34, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee' }} />
                    : <span key={i} style={{ fontSize: 18 }}>📄</span>)}
                </div>
              )}
              {hist.length > 0 && <div style={{ fontSize: 10, color: '#9A6B00', marginTop: 6 }}>↻ {hist.length} earlier revision(s)</div>}
            </div>
          );
          // Strike-off card sits right after Salesman (mig 141) — multi-entry, opens its own modal.
          return key === 'salesman'
            ? [card, <StrikeOffCard key="strikeoff" strikeoffs={data.strikeoffs} photos={photos} onOpen={() => setStrikeoffModal(true)} />]
            : card;
        })}
      </div>

      {/* ── B) TEST REPORTS — four type cards ── */}
      <div style={{ ...secStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>B · Test Reports (FPT / GPT / Wash / Lab)</span>
        <button onClick={() => setTestModal({})} style={btnPrimary}>+ Add Test Report</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
        {[['fpt', 'FPT'], ['gpt', 'GPT'], ['wash', 'Wash Test'], ['lab', 'Lab Test']].map(([key, label]) => {
          const reports = data.tests.filter(t => t.report_type === key);
          const latest = reports[0];
          const failed = ['fpt', 'gpt'].includes(key) && reports.some(t => t.result === 'fail');
          const cardBg = failed ? '#FCEBEB' : latest?.result === 'pass' ? '#EAF7EE' : latest?.result === 'conditional' ? '#FFF8E6' : 'white';
          const cardBorder = failed ? '#fca5a5' : latest?.result === 'pass' ? '#bbe5c5' : latest?.result === 'conditional' ? '#f6d195' : '#eee';
          return (
            <div key={key} style={{ border: `1px solid ${cardBorder}`, borderRadius: 10, padding: '12px 14px', background: cardBg }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 700, fontSize: 13 }}>{label}</span>
                {latest?.result && <span style={tag(latest.result)}>{latest.result.toUpperCase()}</span>}
              </div>
              {failed && <div style={{ marginTop: 6, fontSize: 11, color: '#A32D2D', fontWeight: 800 }}>⚠️ Test Failed — management notified</div>}
              {!latest ? <div style={{ fontSize: 11, color: '#aaa', marginTop: 8 }}>No report uploaded</div>
                : reports.map(t => (
                  <div key={t.id} style={{ fontSize: 11, marginTop: 8, paddingTop: 6, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                    <div><strong>{t.test_house || '—'}</strong> · {t.report_no || '—'} · {fmtD(t.report_date)}
                      {t.result && <span style={{ ...tag(t.result), marginLeft: 6, fontSize: 9, padding: '1px 7px' }}>{t.result.toUpperCase()}</span>}</div>
                    {t.remarks && <div style={{ color: '#666', marginTop: 2 }}>{t.remarks}</div>}
                    {t.file_url
                      ? <a href={fileHref(t.file_url)} target="_blank" rel="noreferrer" style={{ color: '#3B3A8F', fontWeight: 700 }}>👁 View report</a>
                      : <span style={{ color: '#A32D2D' }}>⚠ no file</span>}
                  </div>
                ))}
            </div>
          );
        })}
      </div>

      {/* ── C) BUYER SUBMISSIONS ── */}
      <div style={{ ...secStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>C · Buyer Submissions Log</span>
        <button onClick={() => setSubModal(true)} style={btnPrimary}>+ Add Submission</button>
      </div>
      {data.submissions.length === 0 ? <div style={{ padding: 14, color: '#aaa', fontSize: 13 }}>No submissions logged yet.</div>
        : <div style={{ borderLeft: '2px solid #EEEDF8', marginLeft: 8, paddingLeft: 18 }}>
          {data.submissions.map(s => (
            <div key={s.id} style={{ position: 'relative', marginBottom: 16, background: 'white', border: '1px solid #eee', borderRadius: 8, padding: '10px 14px' }}>
              <div style={{ position: 'absolute', left: -25, top: 14, width: 12, height: 12, borderRadius: 6, background: '#3B3A8F', border: '2px solid white' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{fmtD(s.submission_date)} — {s.what_submitted}</div>
                <span style={tag(s.approval_status)}>{(s.approval_status || 'pending').toUpperCase()}</span>
              </div>
              <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                via {(s.submission_method || '—').replace('_', ' ')}{s.courier_name ? ` · ${s.courier_name} ${s.tracking_no || ''}` : ''} · by {s.created_by_name || '—'}
              </div>
              {photos(s.submission_photos).length > 0 && (
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  {photos(s.submission_photos).map((u, i) => (
                    <a key={i} href={fileHref(u)} target="_blank" rel="noreferrer">
                      {isImg(u) ? <img src={fileHref(u)} alt="" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee' }} /> : <span style={{ fontSize: 20 }}>📄</span>}
                    </a>
                  ))}
                </div>
              )}
              {s.buyer_response && <div style={{ marginTop: 6, fontSize: 12, background: '#F7F6F3', borderRadius: 6, padding: '6px 10px' }}><strong>Buyer ({fmtD(s.buyer_response_date)}):</strong> {s.buyer_response}</div>}
              {s.approval_status === 'pending' && (
                <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                  {['approved', 'conditional', 'rejected'].map(st => (
                    <button key={st} onClick={async () => {
                      const resp = window.prompt(`Buyer response for "${s.what_submitted}" (${st}):`, s.buyer_response || '');
                      if (resp === null) return;
                      await api.patch('/merchdocs/submissions/' + s.id, { approval_status: st, buyer_response: resp || undefined, buyer_response_date: today() }).catch(e => alert(e.response?.data?.error || 'Failed'));
                      load();
                    }} style={{ padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, background: (RESULT_TAG[st] || {}).bg, color: (RESULT_TAG[st] || {}).c }}>
                      {st === 'approved' ? '✓ Approve' : st === 'conditional' ? '◐ Conditional' : '✕ Reject'}</button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>}

      {/* ── D) INSPECTION REPORTS ── */}
      <div style={{ ...secStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>D · Inspection Reports</span>
        <button onClick={() => setInspModal({})} style={btnPrimary}>+ Upload Inspection Report</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
        {INSPECTION_TYPES.map(([key, label]) => {
          const reports = data.inspections.filter(i => i.report_type === key);
          const autoLinked = key === 'aql' ? (data.quality_final_inspections || []) : [];
          return (
            <div key={key} style={{ border: '1px solid #eee', borderRadius: 10, padding: '12px 14px', background: 'white' }}>
              <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6 }}>{label}</div>
              {reports.length === 0 && autoLinked.length === 0 ? <div style={{ fontSize: 11, color: '#aaa' }}>No report uploaded</div>
                : <>
                  {reports.map(r => (
                    <div key={r.id} style={{ fontSize: 11, marginBottom: 6, paddingBottom: 6, borderBottom: '1px solid #f5f5f5' }}>
                      <div>{fmtD(r.inspection_date)} · {r.inspected_by || '—'}{r.aql_result ? ` · AQL: ${r.aql_result}` : ''}</div>
                      {r.file_url ? <a href={fileHref(r.file_url)} target="_blank" rel="noreferrer" style={{ color: '#3B3A8F', fontWeight: 600 }}>👁 View</a> : <span style={{ color: '#A32D2D' }}>⚠ no file</span>}
                    </div>
                  ))}
                  {/* Auto-linked from Quality → Final Inspection for this GPO */}
                  {autoLinked.map(q => (
                    <div key={q.id} style={{ fontSize: 11, marginBottom: 6, paddingBottom: 6, borderBottom: '1px solid #f5f5f5', background: '#EEEDF8', borderRadius: 6, padding: '5px 8px' }}>
                      <div style={{ fontWeight: 700, color: '#3B3A8F' }}>🔗 Auto-linked from Quality module</div>
                      <div>{fmtD(q.inspection_date)} · {q.inspector || '—'} · checked {q.qty_checked} · defects {q.defects_found} ({Number(q.defect_pct)}%)</div>
                    </div>
                  ))}
                </>}
            </div>
          );
        })}
      </div>

      {/* ════ MODALS ════ */}
      {sampleModal && <SampleModal gpoNo={gpoNo} type={sampleModal.type} record={sampleModal.record}
        history={sampleHistory(sampleModal.type)} photos={photos}
        onClose={() => setSampleModal(null)} onSaved={() => { setSampleModal(null); load(); }} saving={saving} setSaving={setSaving} />}
      {strikeoffModal && <StrikeOffModal gpoNo={gpoNo} strikeoffs={data.strikeoffs} photos={photos}
        onClose={() => setStrikeoffModal(false)} onChanged={load} />}
      {testModal && <TestModal gpoNo={gpoNo} onClose={() => setTestModal(null)} onSaved={() => { setTestModal(null); load(); }} />}
      {subModal && <SubmissionModal gpoNo={gpoNo} onClose={() => setSubModal(false)} onSaved={() => { setSubModal(false); load(); }} />}
      {inspModal && <InspectionModal gpoNo={gpoNo} onClose={() => setInspModal(null)} onSaved={() => { setInspModal(null); load(); }} />}
    </div>
  );
}

// ── Sample approval modal: details + photos + decision + revision history ────
function SampleModal({ gpoNo, type, record, history, photos, onClose, onSaved, saving, setSaving }) {
  const label = (SAMPLE_TYPES.find(t => t.key === type) || {}).label || type;
  const [form, setForm] = useState({
    made_date: record?.made_date ? String(record.made_date).slice(0, 10) : '',
    dispatch_date: record?.dispatch_date ? String(record.dispatch_date).slice(0, 10) : '',
    courier_name: record?.courier_name || '', tracking_no: record?.tracking_no || '',
    submission_photos: photos(record?.submission_photos),
    buyer_feedback: record?.buyer_feedback || '',
    buyer_response_date: record?.buyer_response_date ? String(record.buyer_response_date).slice(0, 10) : '',
  });
  const [error, setError] = useState('');
  const st = SAMPLE_STATUS[record?.status] || SAMPLE_STATUS.pending;

  const save = async (status) => {
    setSaving(true); setError('');
    try {
      const body = { ...form, ...(status ? { status, buyer_response_date: form.buyer_response_date || today() } : {}) };
      if (record) {
        const r = await api.patch('/merchdocs/samples/' + record.id, body);
        if ((status === 'revision_requested' || status === 'rejected') && r.data.next_revision)
          alert(`↻ Revision ${r.data.next_revision.revision_no} created — log the new sample against it.`);
      } else {
        await api.post('/merchdocs/samples', { gpo_no: gpoNo, sample_type: type, ...body, status: status || (form.dispatch_date ? 'sent' : 'pending') });
      }
      onSaved();
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 2000, overflowY: 'auto', padding: '24px 0' }}>
      <div style={{ background: 'white', borderRadius: 14, width: '92%', maxWidth: 640, margin: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
        <div style={{ background: '#2D2C72', padding: '14px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.72)', textTransform: 'uppercase', letterSpacing: 1 }}>Sample Approval · GPO {gpoNo}</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'white', marginTop: 2 }}>{label} Sample {record ? `· Rev ${record.revision_no}` : '· New'}
              {record && <span style={{ ...tag(record.status === 'revision_requested' ? 'pending' : record.status), marginLeft: 10 }}>{st.label}</span>}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.85)', fontSize: 22, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ padding: '16px 22px 22px' }}>
          {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{error}</div>}
          {/* Prior rejections — ALWAYS visible on this (new) revision's view, so the team reads why
              earlier rounds failed before resubmitting. Not buried in a collapsed history. */}
          {(() => {
            const priorFails = history.filter(h => h.status === 'rejected' || h.status === 'revision_requested');
            if (!priorFails.length) return null;
            return (
              <div style={{ background: '#FFF4E5', border: '1px solid #F3C77A', borderLeft: '4px solid #E08600', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#8A5A00', marginBottom: 6 }}>⚠ Read before resubmitting — {priorFails.length} prior rejection{priorFails.length > 1 ? 's' : ''}</div>
                {priorFails.map(h => (
                  <div key={h.id} style={{ fontSize: 12, color: '#5A4A2A', marginTop: 4 }}>
                    <strong>Rev {h.revision_no}</strong> · <span style={tag(h.status === 'revision_requested' ? 'pending' : h.status)}>{(SAMPLE_STATUS[h.status] || {}).label || h.status}</span>
                    {h.buyer_response_date ? ` · ${fmtD(h.buyer_response_date)}` : ''}
                    <div style={{ color: '#7A5A2A', marginTop: 2 }}>Reason: {h.buyer_feedback || '—'}</div>
                  </div>
                ))}
              </div>
            );
          })()}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={labelStyle}>Made Date</label><input type="date" style={inputStyle} value={form.made_date} onChange={e => setForm({ ...form, made_date: e.target.value })} /></div>
            <div><label style={labelStyle}>Dispatch Date</label><input type="date" style={inputStyle} value={form.dispatch_date} onChange={e => setForm({ ...form, dispatch_date: e.target.value })} /></div>
            <div><label style={labelStyle}>Courier Name</label><input style={inputStyle} value={form.courier_name} onChange={e => setForm({ ...form, courier_name: e.target.value })} /></div>
            <div><label style={labelStyle}>Tracking No</label><input style={inputStyle} value={form.tracking_no} onChange={e => setForm({ ...form, tracking_no: e.target.value })} /></div>
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={labelStyle}>Submission Photos</label>
            <PhotoUpload value={form.submission_photos} onChange={v => setForm({ ...form, submission_photos: v })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginTop: 12 }}>
            <div><label style={labelStyle}>Buyer Feedback</label><textarea style={{ ...inputStyle, height: 50, resize: 'vertical' }} value={form.buyer_feedback} onChange={e => setForm({ ...form, buyer_feedback: e.target.value })} /></div>
            <div><label style={labelStyle}>Response Date</label><input type="date" style={inputStyle} value={form.buyer_response_date} onChange={e => setForm({ ...form, buyer_response_date: e.target.value })} /></div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button disabled={saving} onClick={() => save(null)} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>💾 Save Details</button>
            {record && !['approved'].includes(record.status) && <>
              <button disabled={saving} onClick={() => save('sent')} style={{ ...btnPrimary, background: '#EEEDF8', color: '#3B3A8F' }}>📦 Mark Sent</button>
              <button disabled={saving} onClick={() => save('revision_requested')} style={{ ...btnPrimary, background: '#FFF8E6', color: '#9A6B00' }}>↻ Request Revision</button>
              <button disabled={saving} onClick={() => save('rejected')} style={{ ...btnPrimary, background: '#E22D2D' }}>✕ Reject</button>
              <button disabled={saving} onClick={() => save('approved')} style={{ ...btnPrimary, background: '#166534' }}>✓ Approve</button>
            </>}
            {!record && <button disabled={saving} onClick={() => save(null)} style={btnPrimary}>+ Create {label} Sample</button>}
          </div>

          {history.length > 0 && (
            <details style={{ marginTop: 16 }}>
              <summary style={{ fontSize: 12, fontWeight: 700, color: '#9A6B00', cursor: 'pointer' }}>↻ Revision history ({history.length})</summary>
              {history.map(h => (
                <div key={h.id} style={{ marginTop: 8, padding: '8px 12px', background: '#F7F6F3', borderRadius: 6, fontSize: 12 }}>
                  <strong>Rev {h.revision_no}</strong> · <span style={tag(h.status === 'revision_requested' ? 'pending' : h.status)}>{(SAMPLE_STATUS[h.status] || {}).label || h.status}</span>
                  · Made {fmtD(h.made_date)} · Dispatched {fmtD(h.dispatch_date)}{h.buyer_response_date ? ` · Buyer response ${fmtD(h.buyer_response_date)}` : ''}
                  {h.buyer_feedback && <div style={{ color: '#666', marginTop: 3 }}>Feedback: {h.buyer_feedback}</div>}
                </div>
              ))}
            </details>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Strike-off card (Section A, after Salesman): aggregate status, opens the modal ──
function StrikeOffCard({ strikeoffs = [], photos, onOpen }) {
  const total = strikeoffs.length;
  const approved = strikeoffs.filter(s => soApproved(s.buyer_response)).length;
  const complete = total > 0 && approved === total;
  const cardBg = total === 0 ? 'white' : complete ? '#EAF7EE' : '#FFF8E6';
  const cardBorder = total === 0 ? '#eee' : complete ? '#bbe5c5' : '#f6d195';
  const thumbs = strikeoffs.flatMap(s => photos(s.proof_photos)).slice(0, 4);
  return (
    <div onClick={onOpen}
      style={{ border: `1px solid ${cardBorder}`, borderRadius: 10, padding: '12px 14px', cursor: 'pointer', background: cardBg, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 700, fontSize: 13 }}>Strike-off <span style={{ fontSize: 10, color: '#888', fontWeight: 600 }}>(print/artwork)</span></span>
        <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700,
          background: total === 0 ? '#f0f2f8' : complete ? '#EAF7EE' : '#FFF8E6',
          color: total === 0 ? '#666' : complete ? '#166534' : '#9A6B00' }}>
          {total === 0 ? 'Not started' : complete ? 'All approved' : 'In progress'}</span>
      </div>
      <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
        {total === 0 ? 'Click to log strike-offs' : `${approved} of ${total} approved`}
      </div>
      {thumbs.length > 0 && (
        <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
          {thumbs.map((u, i) => isImg(u)
            ? <img key={i} src={fileHref(u)} alt="" style={{ width: 34, height: 34, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee' }} />
            : <span key={i} style={{ fontSize: 18 }}>📄</span>)}
        </div>
      )}
    </div>
  );
}

// ── Strike-off modal: list all entries (multiple per GPO) + set buyer response + add new ──
function StrikeOffModal({ gpoNo, strikeoffs = [], photos, onClose, onChanged }) {
  const blank = { artwork_name: '', date_sent: today(), method: 'courier', comments: '', proof_photos: [] };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const add = async () => {
    if (!form.artwork_name.trim()) { setError('Artwork / print name is required'); return; }
    setBusy(true); setError('');
    try {
      await api.post('/merchdocs/strikeoffs', { gpo_no: gpoNo, ...form, artwork_name: form.artwork_name.trim() });
      setForm(blank);
      await onChanged();
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); }
    finally { setBusy(false); }
  };
  const setResponse = async (id, buyer_response) => {
    const comments = window.prompt('Buyer comments (optional):', '');
    if (comments === null) return;   // cancelled
    try {
      await api.patch('/merchdocs/strikeoffs/' + id, { buyer_response, ...(comments.trim() ? { comments: comments.trim() } : {}) });
      await onChanged();
    } catch (err) { alert(err.response?.data?.error || 'Failed'); }
  };

  const total = strikeoffs.length;
  const approved = strikeoffs.filter(s => soApproved(s.buyer_response)).length;

  return (
    <ModalShell title="Strike-off Approvals" sub={`Strike-off (print/artwork) · GPO ${gpoNo}`} onClose={onClose}>
      <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
        {total === 0 ? 'No strike-offs logged. A style with no printed artwork needs none — completion is not penalised.'
          : <><strong>{approved} of {total}</strong> approved. Counts toward completion only when <strong>all</strong> are approved.</>}
      </div>

      {strikeoffs.map(s => (
        <div key={s.id} style={{ border: '1px solid #eee', borderRadius: 8, padding: '10px 12px', marginBottom: 10, background: 'white' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{s.artwork_name}</div>
            <span style={soTag(s.buyer_response)}>{(SO_RESP[s.buyer_response] || SO_RESP.pending).label}</span>
          </div>
          <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
            Sent {fmtD(s.date_sent)}{s.method ? ` · via ${s.method.replace('_', ' ')}` : ''} · by {s.created_by_name || '—'}
          </div>
          {s.comments && <div style={{ marginTop: 6, fontSize: 12, background: '#F7F6F3', borderRadius: 6, padding: '6px 10px' }}>{s.comments}</div>}
          {photos(s.proof_photos).length > 0 && (
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              {photos(s.proof_photos).map((u, i) => (
                <a key={i} href={fileHref(u)} target="_blank" rel="noreferrer">
                  {isImg(u) ? <img src={fileHref(u)} alt="" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee' }} /> : <span style={{ fontSize: 20 }}>📄</span>}
                </a>
              ))}
            </div>
          )}
          <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[['approved', '✓ Approve'], ['approved_with_comments', '◐ Approve w/ comments'], ['rejected', '✕ Reject']].map(([st, lbl]) => (
              <button key={st} onClick={() => setResponse(s.id, st)}
                style={{ padding: '4px 10px', borderRadius: 5, border: s.buyer_response === st ? '2px solid ' + SO_RESP[st].c : '1px solid transparent',
                  cursor: 'pointer', fontSize: 11, fontWeight: 600, background: SO_RESP[st].bg, color: SO_RESP[st].c }}>{lbl}</button>
            ))}
          </div>
        </div>
      ))}

      <div style={{ borderTop: '1px dashed #ddd', marginTop: 6, paddingTop: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#3B3A8F', marginBottom: 10 }}>+ Add strike-off</div>
        {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{error}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Artwork / Print Name *</label>
            <input style={inputStyle} placeholder="e.g. Chest placement print — front logo" value={form.artwork_name} onChange={e => setForm({ ...form, artwork_name: e.target.value })} /></div>
          <div><label style={labelStyle}>Date Sent</label><input type="date" style={inputStyle} value={form.date_sent} onChange={e => setForm({ ...form, date_sent: e.target.value })} /></div>
          <div><label style={labelStyle}>Method</label>
            <select style={inputStyle} value={form.method} onChange={e => setForm({ ...form, method: e.target.value })}>
              {SO_METHODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></div>
          <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Comments</label>
            <textarea style={{ ...inputStyle, height: 44, resize: 'vertical' }} value={form.comments} onChange={e => setForm({ ...form, comments: e.target.value })} /></div>
          <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Proof Photo</label>
            <PhotoUpload value={form.proof_photos} onChange={v => setForm({ ...form, proof_photos: v })} label="Upload proof photo" /></div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 14, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>Close</button>
          <button disabled={busy} onClick={add} style={btnPrimary}>{busy ? 'Saving…' : '+ Add Strike-off'}</button>
        </div>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, sub, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 2000, overflowY: 'auto', padding: '24px 0' }}>
      <div style={{ background: 'white', borderRadius: 14, width: '92%', maxWidth: 560, margin: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
        <div style={{ background: '#2D2C72', padding: '14px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.72)', textTransform: 'uppercase', letterSpacing: 1 }}>{sub}</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'white', marginTop: 2 }}>{title}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.85)', fontSize: 22, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ padding: '16px 22px 22px' }}>{children}</div>
      </div>
    </div>
  );
}

function TestModal({ gpoNo, onClose, onSaved }) {
  const [form, setForm] = useState({ report_type: 'fpt', report_date: today() });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setBusy(true); setError('');
    try { await api.post('/merchdocs/tests', { gpo_no: gpoNo, ...form }); onSaved(); }
    catch (err) { setError(err.response?.data?.error || 'Save failed'); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="Add Test Report" sub={`Test Reports · GPO ${gpoNo}`} onClose={onClose}>
      {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div><label style={labelStyle}>Report Type *</label>
          <select style={inputStyle} value={form.report_type} onChange={e => setForm({ ...form, report_type: e.target.value })}>
            {TEST_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select></div>
        <div><label style={labelStyle}>Result</label>
          <select style={inputStyle} value={form.result || ''} onChange={e => setForm({ ...form, result: e.target.value })}>
            <option value="">— pending —</option><option value="pass">Pass</option><option value="fail">Fail</option><option value="conditional">Conditional</option>
          </select></div>
        <div><label style={labelStyle}>Test House</label><input style={inputStyle} value={form.test_house || ''} onChange={e => setForm({ ...form, test_house: e.target.value })} /></div>
        <div><label style={labelStyle}>Report No</label><input style={inputStyle} value={form.report_no || ''} onChange={e => setForm({ ...form, report_no: e.target.value })} /></div>
        <div><label style={labelStyle}>Report Date</label><input type="date" style={inputStyle} value={form.report_date || ''} onChange={e => setForm({ ...form, report_date: e.target.value })} /></div>
        <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Report File</label><FileUpload value={form.file_url} onChange={u => setForm({ ...form, file_url: u })} /></div>
        <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Remarks</label><input style={inputStyle} value={form.remarks || ''} onChange={e => setForm({ ...form, remarks: e.target.value })} /></div>
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>Cancel</button>
        <button disabled={busy} onClick={save} style={btnPrimary}>{busy ? 'Saving…' : 'Save Test Report'}</button>
      </div>
    </ModalShell>
  );
}

function SubmissionModal({ gpoNo, onClose, onSaved }) {
  const [form, setForm] = useState({ submission_date: today(), submission_method: 'courier', submission_photos: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setBusy(true); setError('');
    try { await api.post('/merchdocs/submissions', { gpo_no: gpoNo, ...form }); onSaved(); }
    catch (err) { setError(err.response?.data?.error || 'Save failed'); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="Add Buyer Submission" sub={`Buyer Submissions · GPO ${gpoNo}`} onClose={onClose}>
      {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div><label style={labelStyle}>Date *</label><input type="date" style={inputStyle} value={form.submission_date} onChange={e => setForm({ ...form, submission_date: e.target.value })} /></div>
        <div><label style={labelStyle}>Method</label>
          <select style={inputStyle} value={form.submission_method} onChange={e => setForm({ ...form, submission_method: e.target.value })}>
            <option value="courier">Courier</option><option value="email">Email</option><option value="whatsapp">WhatsApp</option><option value="hand_delivery">Hand Delivery</option>
          </select></div>
        <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>What Submitted *</label><input style={inputStyle} placeholder="e.g. Lab dip — Navy shade options A/B/C" value={form.what_submitted || ''} onChange={e => setForm({ ...form, what_submitted: e.target.value })} /></div>
        {form.submission_method === 'courier' && <>
          <div><label style={labelStyle}>Courier Name</label><input style={inputStyle} value={form.courier_name || ''} onChange={e => setForm({ ...form, courier_name: e.target.value })} /></div>
          <div><label style={labelStyle}>Tracking No</label><input style={inputStyle} value={form.tracking_no || ''} onChange={e => setForm({ ...form, tracking_no: e.target.value })} /></div>
        </>}
        <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Proof Photos</label>
          <PhotoUpload value={form.submission_photos} onChange={v => setForm({ ...form, submission_photos: v })} label="Upload proof photos" /></div>
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>Cancel</button>
        <button disabled={busy} onClick={save} style={btnPrimary}>{busy ? 'Saving…' : 'Log Submission'}</button>
      </div>
    </ModalShell>
  );
}

function InspectionModal({ gpoNo, onClose, onSaved }) {
  const [form, setForm] = useState({ report_type: 'inline', inspection_date: today() });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setBusy(true); setError('');
    try { await api.post('/merchdocs/inspections', { gpo_no: gpoNo, ...form }); onSaved(); }
    catch (err) { setError(err.response?.data?.error || 'Save failed'); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="Upload Inspection Report" sub={`Inspection Reports · GPO ${gpoNo}`} onClose={onClose}>
      {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div><label style={labelStyle}>Report Type *</label>
          <select style={inputStyle} value={form.report_type} onChange={e => setForm({ ...form, report_type: e.target.value })}>
            {INSPECTION_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select></div>
        <div><label style={labelStyle}>Inspection Date</label><input type="date" style={inputStyle} value={form.inspection_date || ''} onChange={e => setForm({ ...form, inspection_date: e.target.value })} /></div>
        <div><label style={labelStyle}>Inspected By</label><input style={inputStyle} value={form.inspected_by || ''} onChange={e => setForm({ ...form, inspected_by: e.target.value })} /></div>
        <div><label style={labelStyle}>AQL Result</label><input style={inputStyle} placeholder="e.g. Passed AQL 2.5" value={form.aql_result || ''} onChange={e => setForm({ ...form, aql_result: e.target.value })} /></div>
        <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Report File</label><FileUpload value={form.file_url} onChange={u => setForm({ ...form, file_url: u })} /></div>
        <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Remarks</label><input style={inputStyle} value={form.remarks || ''} onChange={e => setForm({ ...form, remarks: e.target.value })} /></div>
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>Cancel</button>
        <button disabled={busy} onClick={save} style={btnPrimary}>{busy ? 'Saving…' : 'Save Inspection Report'}</button>
      </div>
    </ModalShell>
  );
}
